import { Bell, Building2, FileText, Heart, Banknote, History, LayoutDashboard, LifeBuoy, Search, Settings, UserRound, Users, Wallet, Wrench, type LucideIcon } from 'lucide-react'
import { routes } from '@/app/routes'
import { shellStrings as s } from '@/components/navigation/strings'
import type { ProfileType } from '@/types/models'

export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Only highlight on an exact match (for a root route that has children). */
  end?: boolean
  /** Paths under `to` that belong to another item (Properties must not light up on Interests). */
  exclude?: string[]
}

export interface NavSection {
  /** Omitted for the first, unlabelled group. */
  label?: string
  items: NavItem[]
}

export function navItemsFor(profile: ProfileType): NavItem[] {
  if (profile === 'landlord') {
    return [
      { to: routes.landlord.dashboard, label: s.dashboard, icon: LayoutDashboard },
      { to: routes.landlord.properties, label: s.properties, icon: Building2 },
      { to: routes.landlord.leases, label: s.leases, icon: FileText },
      { to: routes.landlord.transactions, label: s.transactions, icon: Banknote },
      { to: routes.account, label: s.settings, icon: Settings, end: true },
    ]
  }
  return [
    { to: routes.tenant.dashboard, label: s.home, icon: LayoutDashboard },
    { to: routes.tenant.browse, label: s.browse, icon: Search },
    { to: routes.tenant.transactions, label: s.transactions, icon: Banknote },
    { to: routes.notifications, label: s.notifications, icon: Bell },
    { to: routes.account, label: s.settings, icon: Settings, end: true },
  ]
}

export function sidebarSectionsFor(profile: ProfileType): NavSection[] {
  if (profile === 'landlord') {
    return [
      { items: [{ to: routes.landlord.dashboard, label: s.dashboard, icon: LayoutDashboard }] },
      {
        label: s.portfolio,
        items: [
          { to: routes.landlord.properties, label: s.properties, icon: Building2, exclude: [routes.landlord.propertyInterests] },
          { to: routes.landlord.propertyInterests, label: s.interests, icon: Heart },
          { to: routes.landlord.leases, label: s.leases, icon: FileText },
          { to: routes.landlord.tenants, label: s.tenants, icon: Users },
          { to: routes.landlord.maintenance, label: s.maintenance, icon: Wrench },
        ],
      },
      {
        label: s.money,
        items: [
          { to: routes.landlord.transactions, label: s.transactions, icon: Banknote },
          { to: routes.landlord.subscriptions, label: s.subscription, icon: Wallet },
        ],
      },
      { label: s.account, items: accountItems },
    ]
  }
  return [
    {
      items: [
        { to: routes.tenant.dashboard, label: s.home, icon: LayoutDashboard },
        { to: routes.tenant.browse, label: s.browse, icon: Search },
      ],
    },
    {
      label: s.renting,
      items: [
        { to: routes.tenant.transactions, label: s.transactions, icon: Banknote },
        { to: routes.tenant.interests, label: s.myInterests, icon: Heart },
        { to: routes.tenant.leaseHistory, label: s.leaseHistory, icon: History },
        { to: routes.tenant.profile, label: s.myProfile, icon: UserRound },
      ],
    },
    { label: s.account, items: accountItems },
  ]
}

const accountItems: NavItem[] = [
  { to: routes.notifications, label: s.notifications, icon: Bell },
  { to: routes.support, label: s.help, icon: LifeBuoy },
  { to: routes.account, label: s.settings, icon: Settings, exclude: [routes.support] },
]

export function isNavActive(item: NavItem, pathname: string): boolean {
  if (item.exclude?.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return false
  if (item.end) return pathname === item.to
  return pathname === item.to || pathname.startsWith(`${item.to}/`)
}
